/**
 * "Własnym tempem" — three days in order, rest between them, and a cursor that moves only once the
 * day before it is finished. The phone's `SelfPacedSequenceViz`, transcribed.
 *
 * Its argument is the opposite of `CalendarWeekViz`'s and it is made in the same beat: nothing is
 * booked, nothing is missed, and the next session unlocks behind the last one
 * (`self-paced-plan-requirements.md` R8). Shown while the trainer is choosing the mode, and again
 * as the assign dialog's whole projection — a self-paced assignment has no dates to draw.
 *
 * Fixed content, no data. See `CalendarWeekViz` for why, and `planSystemLoop.ts` for the stops and
 * the two deviations from the mock's CSS.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SEQ_ACTIVE_RING,
  SEQ_CHECK,
  SEQ_DRIFT,
  SEQ_HALO,
  SEQ_LABEL_IN,
  SEQ_LABEL_OUT,
  SEQ_WAKE_RING,
  cursorTrack,
} from './planSystemLoop';
import { useMeasuredLoop } from './useMeasuredLoop';
import type { PlanSystemVizProps } from './CalendarWeekViz';

import styles from './planSystemViz.module.css';

export function SelfPacedSequenceViz({ reducedMotion, running, foot = true }: PlanSystemVizProps) {
  const { t } = useTranslation();
  const row = useRef<HTMLDivElement>(null);
  const cursor = useRef<HTMLSpanElement>(null);
  const check = useRef<HTMLSpanElement>(null);
  const activeRing = useRef<HTMLSpanElement>(null);
  const wakeRing = useRef<HTMLSpanElement>(null);
  const halo = useRef<HTMLSpanElement>(null);
  const labelOut = useRef<HTMLSpanElement>(null);
  const labelIn = useRef<HTMLSpanElement>(null);
  const firstRule = useRef<HTMLSpanElement>(null);
  const secondRule = useRef<HTMLSpanElement>(null);

  /**
   * The cursor's travel is the distance between day one's centre and day two's, measured.
   *
   * The mock writes `calc(50cqw - 30px)`, which is the same distance expressed as "half the row,
   * less half a tile" — true only while day two is exactly centred. Measuring the two tiles says
   * what the drawing actually shows.
   */
  useMeasuredLoop(
    row,
    (box) => {
      const days = [...box.querySelectorAll<HTMLElement>(`.${String(styles.seqDay)}`)];
      const centre = (element: HTMLElement | undefined) =>
        element === undefined ? 0 : element.offsetLeft + element.offsetWidth / 2;
      const travel = centre(days[1]) - centre(days[0]);

      return [
        { elements: [cursor.current], track: cursorTrack(travel) },
        { elements: [check.current], track: SEQ_CHECK },
        { elements: [activeRing.current], track: SEQ_ACTIVE_RING },
        { elements: [wakeRing.current], track: SEQ_WAKE_RING },
        { elements: [halo.current], track: SEQ_HALO },
        { elements: [labelOut.current], track: SEQ_LABEL_OUT },
        { elements: [labelIn.current], track: SEQ_LABEL_IN },
        { elements: [firstRule.current, secondRule.current], track: SEQ_DRIFT },
      ];
    },
    { reducedMotion, running },
  );

  return (
    <div className={styles.viz}>
      <div className={styles.sequence} role="img" aria-label={t('web:planSystemViz.sequence.alt')}>
        <span className={styles.cursor} ref={cursor} aria-hidden="true" />
        <div className={styles.seqRow} ref={row} aria-hidden="true">
          <div className={`${String(styles.seqDay)} ${String(styles.seqDone)}`}>
            <span className={styles.seqRing} ref={activeRing} />
            <span className={styles.day}>{t('web:planSystemViz.sequence.day', { n: 1 })}</span>
            <span className={styles.seqMark}>
              <span className={styles.seqCheck} ref={check}>
                <TickGlyph />
              </span>
            </span>
            <span className={styles.name}>{t('web:planSystemViz.sequence.name.one')}</span>
          </div>

          <div className={styles.rest}>
            <span className={styles.rule} ref={firstRule} />
            <span className={styles.restLabel}>
              {t('web:planSystemViz.sequence.rest', { count: 1 })}
            </span>
          </div>

          <div className={`${String(styles.seqDay)} ${String(styles.seqNext)}`}>
            <span className={styles.seqRing} ref={wakeRing} />
            <span className={styles.halo} ref={halo} />
            <span className={styles.day}>{t('web:planSystemViz.sequence.day', { n: 2 })}</span>
            <span className={styles.seqMark} />
            <span className={styles.name}>
              <span className={styles.nameLayer} ref={labelOut}>
                {t('web:planSystemViz.sequence.name.two')}
              </span>
              <span className={`${String(styles.nameLayer)} ${String(styles.nameWaiting)}`} ref={labelIn}>
                {t('web:planSystemViz.sequence.waiting')}
              </span>
            </span>
          </div>

          <div className={styles.rest}>
            <span className={styles.rule} ref={secondRule} />
            <span className={styles.restLabel}>
              {t('web:planSystemViz.sequence.rest', { count: 2 })}
            </span>
          </div>

          <div className={styles.seqDay}>
            <span className={styles.day}>{t('web:planSystemViz.sequence.day', { n: 3 })}</span>
            <span className={styles.seqMark} />
            <span className={styles.name}>{t('web:planSystemViz.sequence.name.three')}</span>
          </div>
        </div>
      </div>
      {foot && (
        <p className={styles.foot}>
          <b className={styles.figure}>{t('web:planSystemViz.sequence.figure')}</b>
          <span>{t('web:planSystemViz.sequence.footNote')}</span>
        </p>
      )}
    </div>
  );
}

function TickGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
